import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types/UserRole';
import AccessRestrictedPage from './AccessRestrictedPage';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles: Array<UserRole>;
  /** Opcional: mostrar tela bloqueio ou redirect? Padrão = redirect pro home do user */
  showRestrictedPage?: boolean;
}

/**
 * Segunda camada de proteção (depois do routes.tsx).
 * Valida:
 *  1. isAuthenticated (user != null)
 *  2. user.role está dentro de allowedRoles (garante acesso por perfil)
 *
 * Como usar:
 * <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
 *   <AdminLayout><Routes>...</Routes></AdminLayout>
 * </ProtectedRoute>
 */
const ProtectedRoute = ({
  children,
  allowedRoles,
  showRestrictedPage = false,
}: ProtectedRouteProps) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 text-gray-600">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          <span>Carregando...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const hasAllowedRole = allowedRoles.some(
    (allowed) => String(user.role).toLowerCase() === String(allowed).toLowerCase(),
  );

  if (!hasAllowedRole) {
    if (showRestrictedPage) {
      const homeByRole: Record<string, string> = {
        [UserRole.ADMIN]: '/admin/dashboard',
        [UserRole.CLIENT]: '/client/dashboard',
        [UserRole.USER]: '/user/historico-viagens',
      };
      const safeRedirect = homeByRole[String(user.role).toLowerCase()] ?? '/login';
      return (
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="mx-auto flex min-h-[calc(100vh-3rem)] max-w-3xl items-center justify-center">
            <div className="w-full">
              <AccessRestrictedPage
                title="Acesso negado"
                message={`Seu perfil (${String(user.role).toUpperCase()}) não tem permissão para acessar esta página. Você será redirecionado automaticamente.`}
              />
              <div className="mt-4 flex justify-center">
                <a
                  href={safeRedirect}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Voltar para o início
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const homeByRole: Record<string, string> = {
      [UserRole.ADMIN]: '/admin/dashboard',
      [UserRole.CLIENT]: '/client/dashboard',
      [UserRole.USER]: '/user/historico-viagens',
    };
    const safeRedirect = homeByRole[String(user.role).toLowerCase()] ?? '/login';
    return <Navigate to={safeRedirect} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
